// ── UCC Searches ─────────────────────────────────────────────────────────────
//
// Searches are how we check a filing is still doing its job. Anyone can file a
// UCC-3 against our financing statement — a termination needs no signature
// from us — so a filing in the register can be "Active" here and terminated at
// the Secretary of State. A search is the only thing that sees that.
//
// Each search ordered through CSC lands here. Its report (CSC only gives the
// results as a PDF) is read back into rows, and each one is checked:
//
//   still of record   our filing is on it, not terminated / amended / assigned
//                     by anyone else, not lapsed
//   first position    no other active lien filed ahead of ours
//   pre-funding       a search run before (or around) funding that does not show
//                     us yet was run to see who had to be paid off and
//                     terminated — it lists the liens active then, nothing more
//
// Like the CSC queue on UCC Filings, a search has to be tied to a borrower by a
// person: the name searched ("hobbs bonded fiber") is matched to a borrower,
// and the match waits in "To tie out" until someone confirms or corrects it.
//
// Nothing here orders a search or files anything. Searches are ordered on
// CSC's website; they arrive on the next Pull from CSC (UCC Filings page).
import { useState, useEffect, Fragment } from "react";
import { B } from "../../../shared/theme";
import { fmtDate, localFilter, exportCSV } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, Modal, SearchBar, SectionHeader, Spinner, STh, Td, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { LastRunTile, runOf, useMonitorFreshness } from "../components/LastRunTile";
import { CscDocLinks } from "../components/CscDocLinks";

const RED = { bg: "#FBE4DD", fg: "#993C1D" };
const AMBER = { bg: "#FDF0D5", fg: "#7A5200" };
const GREEN = { bg: "#E1F5EE", fg: "#0F6E56" };
const GREY = { bg: "#F1EFE8", fg: "#5F5E5A" };
const INFO = { bg: B.cyanLight, fg: B.cyanDark };
const TONE = { red: RED, amber: AMBER, green: GREEN, info: INFO, grey: GREY };
const muted = { color: "var(--color-text-secondary)" };
const mono = { fontFamily: "var(--font-mono)", fontSize: 12 };
// Title of a card inside an open borrower (Our filings, Searches).
const panelTitle = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                     color: B.cyanDark, marginBottom: 6 };

const Pill = ({ tone, children, title }) => (
  <span title={title} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                               background: tone.bg, color: tone.fg, display: "inline-block" }}>{children}</span>
);
// One rule for every tile:
//   SHADED   = something is waiting there — a to-do tile (it has a tone) with a
//              count above zero. Amber for work to do, red for a risk. Count
//              tiles (Borrowers, All searches) are never shaded.
//   OUTLINED = the list showing below. Selecting a tile never changes its colour.
const Tile = ({ label, value, tone, onClick, selected, hint }) => {
  const lit = !!tone && Number(value) > 0;
  return <div onClick={onClick} title={hint}
       style={{ background: lit ? tone : "var(--color-background-secondary)",
                border: selected ? "2px solid " + B.cyan : "0.5px solid " + (lit ? "transparent" : "var(--color-border-tertiary)"),
                padding: selected ? "8.5px 12.5px" : "10px 14px",
                borderRadius: 8, cursor: onClick ? "pointer" : "default", minWidth: 120 }}>
    <div style={{ fontSize: 11, ...muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</div>
  </div>;
};
const Band = ({ children, colSpan, onClick }) => (
  <tr onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }}>
    <td colSpan={colSpan} style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                                   letterSpacing: "0.06em", color: B.cyanDark, background: B.cyanLight }}>{children}</td>
  </tr>
);

const STATUS_ORDER = ["Accrual", "Non Accrual", "Funding in Progress", "Charged Off", "Terminated"];
const statusRank = st => { const i = STATUS_ORDER.indexOf(st); return i >= 0 ? i : /paid/i.test(st || "") ? 99 : 50; };
const parseDocs = d => { try { const a = typeof d === "string" ? JSON.parse(d) : d; return Array.isArray(a) ? a : []; } catch (e) { return []; } };
const orderNo = id => String(id || "").replace(/-\d+$/, "");
const fmtWhen = t => { if (!t) return "—"; const d = new Date(t); return isNaN(d) ? String(t) : d.toLocaleString(); };
// A report the reader didn't turn into rows. Usually that is simply not a UCC
// Summary search (document retrieval, copy requests …) — nothing to check
// automatically, and not an error. Only an actual summary whose layout the
// reader couldn't follow is worth a look by eye.
const notSummary = o => /not a ucc summary report|lists no report/i.test((o.report && o.report.error) || "")
  || /retrieval|copy|copies|document/i.test(o.filingtype || "");
const searchDate = o => (o.analysis && o.analysis.asOf) || (o.report && o.report.currentasof) || o.completed || o.filingdate;
// Only the most recent read search per borrower and state carries alerts; an
// older one is history — its findings are shown for reference, never as open.
const isSuperseded = o => !!(o.analysis && o.analysis.read === "ok" && !o.latest && !o.needsreview);
// A borrower's searches worth showing by default: per state, the latest read
// one and anything newer (not read yet, unreadable). Older ones are behind "show older".
// Searches marked not applicable are always kept — SearchTable tucks them into
// their own collapsed section, so "show older" never hides or counts them.
export const currentOnly = list => {
  const latestByState = {};
  list.filter(o => o.latest).forEach(o => { latestByState[o.jurisdiction_state || ""] = String(searchDate(o) || ""); });
  return list.filter(o => {
    if (o.notapplicable) return true;
    const cut = latestByState[o.jurisdiction_state || ""];
    return o.latest || o.needsreview || cut == null || String(searchDate(o) || "") > cut;
  });
};

// The one-line result of a search, for every table.
export const SearchResult = ({ o }) => {
  const a = o.analysis || {};
  if (o.notapplicable) return <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
      title={naReason(o) || "Marked not applicable"}><Pill tone={GREY}>not applicable</Pill>
      <span style={{ fontSize: 11.5, ...muted }}>{naReason(o)}</span></span>;
  if (!o.suggested_accountid && a.read !== "error" && a.read !== "nodoc") {
    return o.report && o.report.status === "ok"
      ? <span style={{ fontSize: 12, ...muted }}>{o.report.totalrecords ?? "?"} record{o.report.totalrecords === 1 ? "" : "s"} · tie to a borrower to check</span>
      : <span style={{ fontSize: 12, ...muted }}>—</span>;
  }
  if (a.read === "unread") return o.hasdocs ? <span style={{ fontSize: 12, ...muted }}>report not read yet</span>
                                            : <span style={{ fontSize: 12, ...muted }} title="CSC's API returned no report file for this search">no report from CSC</span>;
  if (a.read === "nodoc") return <span style={{ fontSize: 12, ...muted }}>no report from CSC</span>;
  if (a.read === "error") return notSummary(o)
    ? <span style={{ fontSize: 12, ...muted }} title="Not a UCC Summary search — nothing to check automatically. The documents are in the search.">documents only · no summary</span>
    : <span style={{ fontSize: 12, ...muted }} title={(o.report && o.report.error) || ""}>summary not read · review by eye</span>;
  if (isSuperseded(o)) return <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
      title="A newer search of this borrower in this state has been read — only the latest one carries alerts">
    <Pill tone={GREY}>superseded</Pill><span style={{ fontSize: 11.5, ...muted }}>{a.headline}</span></span>;
  return <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
    <Pill tone={TONE[a.tone] || GREY}>{a.headline}</Pill>
    {a.open > 0 && a.mode !== "prefunding" && <span style={{ fontSize: 11.5, color: a.tone === "red" ? RED.fg : AMBER.fg }}>{a.open} open</span>}
  </span>;
};

// ── A list of searches (the page, a borrower's searches, the Monitoring tab) ──
// Orders CSC returned no report for — almost always rejected for missing
// information — are kept out of the list, in a collapsed section at the bottom.
// Searches marked NOT APPLICABLE (run by mistake — wrong name, wrong state)
// get a collapsed section of their own, below that.
const isNoReport = o => !o.notapplicable && (!!o.noreport || (o.analysis && o.analysis.read === "nodoc") || (!o.hasdocs && !o.report));
const naReason = o => o.tracking ? "Debtor Tracking — results come by email"
  : o.nosummary ? "No UCC Summary Report — documents only"
  : String(o.reviewnote || "").replace(/^not applicable:?\s*/i, "");
export const SearchTable = ({ rows, onOpen, showBorrower, defaultKey }) => {
  const [showNR, setShowNR] = useState(false);
  const [showNA, setShowNA] = useState(false);
  const na = rows.filter(o => o.notapplicable);
  const main = rows.filter(o => !o.notapplicable && !isNoReport(o));
  const nr = rows.filter(isNoReport);
  return <>
    {main.length > 0 && <SearchRows rows={main} onOpen={onOpen} showBorrower={showBorrower} defaultKey={defaultKey} />}
    {!main.length && <div style={{ fontSize: 12.5, ...muted, padding: "6px 0" }}>No searches with a report.</div>}
    {nr.length > 0 && <div style={{ marginTop: 8 }}>
      <div onClick={() => setShowNR(x => !x)}
           style={{ cursor: "pointer", display: "flex", alignItems: "baseline", gap: 10, padding: "6px 12px", background: "var(--color-background-secondary)", borderRadius: 4 }}>
        <strong style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", ...muted }}>
          {showNR ? "▾" : "▸"} No report from CSC ({nr.length})</strong>
        <span style={{ fontSize: 11.5, ...muted }}>Likely orders CSC rejected for missing information. Not counted anywhere.</span>
      </div>
      {showNR && <div style={{ opacity: 0.75 }}><SearchRows rows={nr} onOpen={onOpen} showBorrower={showBorrower} defaultKey={defaultKey} /></div>}
    </div>}
    {na.length > 0 && <div style={{ marginTop: 8 }}>
      <div onClick={() => setShowNA(x => !x)}
           style={{ cursor: "pointer", display: "flex", alignItems: "baseline", gap: 10, padding: "6px 12px", background: "var(--color-background-secondary)", borderRadius: 4 }}>
        <strong style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", ...muted }}>
          {showNA ? "▾" : "▸"} Not applicable ({na.length})</strong>
        <span style={{ fontSize: 11.5, ...muted }}>Orders with no UCC Summary Report to read — Debtor Tracking (results come by email), copies and other document orders — and searches marked as run by mistake. Not counted anywhere; open one for its documents.</span>
      </div>
      {showNA && <div style={{ opacity: 0.75 }}><SearchRows rows={na} onOpen={onOpen} showBorrower={showBorrower} defaultKey={defaultKey} /></div>}
    </div>}
  </>;
};
const SearchRows = ({ rows, onOpen, showBorrower, defaultKey }) => {
  const withKeys = rows.map(o => ({ ...o, _date: searchDate(o), _by: o.searchedby || o.cscuser,
                                    _result: (o.analysis && o.analysis.headline) || "" }));
  const { sorted, sortKey, sortDir, toggle } = useSortable(withKeys, defaultKey || "_date", "desc");
  const H = (label, k) => <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} />;
  return <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
    <thead><tr>
      {H("Results as of", "_date")}{H("CSC Order #", "csc_orderid")}{H("State", "jurisdiction_state")}
      {H("Name searched", "debtornames")}{showBorrower && H("Borrower", "suggested_company")}
      {H("Result", "_result")}{H("Ordered by", "_by")}{H("Billing ref", "billingref")}<Th></Th>
    </tr></thead>
    <tbody>{sorted.map(o => (
      <tr key={o.csc_orderid} style={{ cursor: "pointer" }} onClick={() => onOpen(o)}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--color-background-secondary)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = ""; }}>
        <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(o._date)}</Td>
        <Td style={mono}>{orderNo(o.csc_orderid)}</Td>
        <Td>{o.jurisdiction_state || "—"}</Td>
        <Td style={{ fontWeight: 500 }}>{o.debtornames || "—"}</Td>
        {showBorrower && <Td>{o.suggested_company || <span style={muted}>—</span>}
          {o.needsreview && o.suggested_accountid && <div style={{ fontSize: 11, ...muted }}>matched from the name — not confirmed</div>}</Td>}
        <Td><SearchResult o={o} /></Td>
        <Td>{o._by || "—"}</Td>
        <Td>{o.billingref || "—"}</Td>
        <Td style={{ textAlign: "right", whiteSpace: "nowrap" }}><span style={{ color: B.cyan, fontSize: 12 }}>open →</span></Td>
      </tr>))}</tbody>
  </table>;
};

// ── One search: what was searched, what the report says, what it means ──
export const SearchDetail = ({ o, accounts, onClose, onChanged }) => {
  const { api, role } = useApp();
  const [busy, setBusy] = useState(null);
  const [showHist, setShowHist] = useState(false);
  const [err, setErr] = useState(null);
  const a = o.analysis || {};
  const r = o.report;
  const docs = parseDocs(o.documents).map(d => ({ order: o.csc_orderid, id: d.id, description: d.description, eventtype: "Search" }));
  const run = async (key, fn) => {
    setBusy(key); setErr(null);
    // Refresh the page underneath after every action — a failed read is stored
    // too (as "couldn't read report"), so it refreshes on an error as well.
    try { await fn(); } catch (e) { setErr(e.message); }
    finally { onChanged && onChanged(); }
    setBusy(null);
  };
  const read = () => run("read", async () => {
    const x = await api.post("readUCCSearchReport", { csc_orderid: o.csc_orderid });
    if (x && x.status !== "ok") throw new Error(x.error || "The report could not be read.");
  });
  const assign = accountid => run("assign", () => api.post("saveCSCOrderReview", { csc_orderid: o.csc_orderid, action: "assignSearch", accountid: accountid || null }));
  const confirm = () => run("assign", () => api.post("saveCSCOrderReview", { action: "confirmSearches", ids: [o.csc_orderid] }));
  const ack = f => {
    const note = window.prompt("Why is this known / acceptable? (e.g. subordinated under the intercreditor, equipment-only lien)", "");
    if (note === null) return;
    run("ack" + f.key, () => api.post("saveCSCOrderReview", { action: "ackFinding", accountid: o.suggested_accountid, findingkey: f.key, note,
      label: `${o.jurisdiction_state || ""} search, results as of ${fmtDate(searchDate(o))}: ${f.text}` }));
  };
  const clearAck = f => run("ack" + f.key, () => api.post("saveCSCOrderReview", { action: "clearAck", accountid: o.suggested_accountid, findingkey: f.key }));
  // Correcting "Ours" on a filing group: kept for this borrower by filing number,
  // so later searches showing the same filing get the same answer.
  const setOurs = (g, ours) => {
    const note = window.prompt(ours ? `Why is #${g.number} ours?` : `Why is #${g.number} not ours? (e.g. entered in the register by mistake)`, "");
    if (note === null) return;
    run("ours" + g.group, () => api.post("saveCSCOrderReview", { action: "ackFinding", accountid: o.suggested_accountid,
      findingkey: (ours ? "ours:" : "notours:") + g.matchKey, note,
      label: `${g.secured || "secured party not shown"}, ${o.jurisdiction_state || ""} search` }));
  };
  const undoOurs = g => run("ours" + g.group, () => api.post("saveCSCOrderReview", { action: "clearAck", accountid: o.suggested_accountid,
    findingkey: g.oursSet + ":" + g.matchKey }));
  // Run by mistake (wrong name, wrong state): kept, but counted nowhere.
  const notApplicable = () => {
    const note = window.prompt("Why doesn't this search apply? (e.g. wrong name — not our borrower's legal name; wrong state)",
                               "Run by mistake — wrong name");
    if (note === null) return;
    run("na", () => api.post("saveCSCOrderReview", { csc_orderid: o.csc_orderid, action: "notApplicable", note }));
  };
  const restore = () => run("na", () => api.post("saveCSCOrderReview", { csc_orderid: o.csc_orderid, action: "restore" }));
  const Fact = ({ label, children }) => <div style={{ fontSize: 12.5, padding: "3px 0", display: "flex", gap: 8 }}>
    <span style={{ ...muted, minWidth: 130 }}>{label}</span><span style={{ fontWeight: 500 }}>{children || "—"}</span></div>;
  const groups = a.groups || [];

  return <Modal title={"UCC Search · " + (o.debtornames || orderNo(o.csc_orderid))} onClose={onClose} wide>
    {err && <div style={{ fontSize: 12.5, background: RED.bg, color: RED.fg, borderRadius: 6, padding: "8px 12px", marginBottom: 12 }}>{err}</div>}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "4px 24px", marginBottom: 14 }}>
      <div>
        <Fact label="CSC Order #"><span style={mono}>{orderNo(o.csc_orderid)}</span></Fact>
        <Fact label="Search">{o.filingtype}</Fact>
        <Fact label="Where">{[o.jurisdiction_state, o.jurisdiction_office].filter(Boolean).join(" · ")}</Fact>
        <Fact label="Name searched">{o.debtornames}</Fact>
        {r && r.criteria && r.criteria !== o.debtornames && <Fact label="Criteria">{r.criteria}</Fact>}
      </div>
      <div>
        <Fact label="Results as of">{r && r.currentasof ? fmtDate(r.currentasof) : null}</Fact>
        <Fact label="Start date">{r && r.startdate ? fmtDate(r.startdate) : null}</Fact>
        <Fact label="Lapsed included">{r && r.includelapsed != null ? (r.includelapsed ? "Yes" : "No") : null}</Fact>
        <Fact label="Ordered by">{o.searchedby || o.cscuser}</Fact>
        <Fact label="Billing ref">{o.billingref}</Fact>
      </div>
    </div>

    {o.notapplicable && <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 12px", borderRadius: 6,
                                      background: GREY.bg, color: GREY.fg, marginBottom: 14, fontSize: 12.5 }}>
      <strong>Not applicable</strong>
      <span>{o.tracking
        ? "A Debtor Tracking order — a standing watch, not a search. Its results come by email and are read on the Tracking alerts tile"
        : o.nosummary ? "No UCC Summary Report on this order — copies or other documents only, so there is nothing to read. Its documents open below"
        : (naReason(o) || "Marked as run by mistake") + (o.reviewedby ? " — " + o.reviewedby : "")}. Not counted anywhere.</span>
      {role.canEdit && !o.nosummary && <span style={{ marginLeft: "auto" }}><Btn disabled={!!busy} onClick={restore}>Restore</Btn></span>}
    </div>}

    {/* Borrower: the match from the name, a person's confirmation, or "not a borrower". */}
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 12px", borderRadius: 6,
                  background: o.needsreview ? AMBER.bg : "var(--color-background-secondary)", marginBottom: 14 }}>
      <strong style={{ fontSize: 12.5 }}>Borrower</strong>
      {role.canEdit
        ? <select value={o.suggested_accountid || ""} disabled={!!busy} onChange={e => assign(e.target.value)}
                  style={{ padding: "4px 6px", fontSize: 12.5, borderRadius: 4, border: "1px solid #999", maxWidth: 320 }}>
            <option value="">— not a borrower —</option>
            {o.suggested_accountid && !(accounts || []).some(x => String(x.id) === String(o.suggested_accountid)) &&
              <option value={o.suggested_accountid}>{o.suggested_company || o.suggested_accountid}</option>}
            {(accounts || []).map(x => <option key={x.id} value={x.id}>{x.companyname}</option>)}
          </select>
        : <span style={{ fontSize: 12.5 }}>{o.suggested_company || "—"}</span>}
      {o.needsreview
        ? <><span style={{ fontSize: 12, color: AMBER.fg }}>{o.suggested_accountid ? "Matched from the name searched — confirm or change." : "No match — pick the borrower, or leave as not a borrower."}</span>
            {role.canEdit && o.suggested_accountid && <Btn variant="primary" disabled={!!busy} onClick={confirm}>Confirm</Btn>}
            {role.canEdit && !o.suggested_accountid && <Btn disabled={!!busy} onClick={() => assign(null)}>Not a borrower</Btn>}</>
        : !o.notapplicable && <span style={{ fontSize: 12, ...muted }}>{o.reviewnote === "not a borrower" ? "Marked not a borrower" : "Confirmed"}{o.reviewedby ? " by " + o.reviewedby : ""}</span>}
      {role.canEdit && !o.notapplicable &&
        <span style={{ marginLeft: "auto" }}><Btn disabled={!!busy} onClick={notApplicable}
          title="Run by mistake (wrong name, wrong state) — keep it, but count it nowhere">Not applicable</Btn></span>}
    </div>

    {/* The report and what it says. */}
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
      <strong style={{ fontSize: 13 }}>Report</strong>
      {docs.length ? <CscDocLinks api={api} docs={docs} compact /> : <span style={{ fontSize: 12, ...muted }}>CSC returned no report file for this search.</span>}
      {r && <span style={{ fontSize: 11.5, ...muted }}>read {fmtWhen(r.readat)}{r.readby ? " by " + r.readby : ""}</span>}
      {role.canEdit && docs.length > 0 &&
        <span style={{ marginLeft: "auto" }}><Btn disabled={!!busy} onClick={read}>{busy === "read" ? "Reading…" : r ? "Re-read report" : "Read report"}</Btn></span>}
    </div>
    {r && r.status === "error" && (notSummary(o)
      ? <div style={{ fontSize: 12.5, ...muted, marginBottom: 10 }}>
          This order has no UCC Summary Report (it's a {o.filingtype || "document"} order), so there is nothing to check
          automatically. Open the documents above.</div>
      : <div style={{ fontSize: 12.5, ...muted, marginBottom: 10 }}>
          The summary report's layout wasn't one the reader knows, so it wasn't checked automatically — open it above
          and review by eye. <span style={{ fontSize: 11.5 }}>({r.error})</span></div>)}

    {a.read === "ok" && <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "6px 0 10px" }}>
        <Pill tone={isSuperseded(o) ? GREY : TONE[a.tone] || GREY}>{a.headline}</Pill>
        <span style={{ fontSize: 12, ...muted }}>
          {a.mode === "prefunding"
            ? "Run before funding (or around it, before our filing showed) — it shows who had to be paid off and terminated."
            : "Checks: our filing is on it, nobody else has terminated / amended / assigned it, it hasn't lapsed, and no active lien was filed ahead of ours."}
          {isSuperseded(o) ? " SUPERSEDED — a newer search of this borrower in this state has been read. Its findings are shown for reference only; nothing here needs action." : ""}
        </span>
      </div>
      {a.findings.length > 0 && <div style={{ marginBottom: 14 }}>
        {a.findings.map(f => {
          const old = isSuperseded(o);
          const tone = f.acked || old ? GREY : f.level === "red" ? RED : f.level === "amber" ? AMBER : INFO;
          return <div key={f.key} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "7px 10px", marginBottom: 4,
                                         borderRadius: 6, background: tone.bg, color: tone.fg, fontSize: 12.5 }}>
            <span style={{ flex: 1, textDecoration: f.acked ? "line-through" : "none" }}>{f.text}
              {f.ack && <div style={{ fontSize: 11.5, textDecoration: "none", marginTop: 2 }}>
                Known — {f.ack.note || "no note"} ({f.ack.by}, {fmtDate(String(f.ack.at || ""))})</div>}</span>
            {role.canEdit && o.suggested_accountid && !old && (f.level === "red" || f.level === "amber") && (
              f.acked
                ? <Btn disabled={!!busy} onClick={() => clearAck(f)}>Re-open</Btn>
                : <Btn disabled={!!busy} onClick={() => ack(f)} title="Known and acceptable for this borrower — later searches won't raise it again">Mark known</Btn>)}
          </div>;
        })}
      </div>}
    </>}

    {groups.length > 0 && <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead><tr><Th>Group</Th><Th>Filing #</Th><Th>Type</Th><Th>Filed</Th><Th>Lapses</Th><Th>Secured party</Th><Th>Debtor</Th><Th></Th></tr></thead>
        <tbody>{groups.map(g => <Fragment key={g.group}>
          {g.recs.map((x, i) => (
            <tr key={g.group + i} style={{ background: g.ours ? "#F0FAF6" : undefined, opacity: g.active ? 1 : 0.6 }}>
              <Td style={mono}>{x.groupid}</Td>
              <Td style={mono}>{x.filingnumber}</Td>
              <Td>{x.category}</Td>
              <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(x.filingdate)}</Td>
              <Td style={{ whiteSpace: "nowrap" }}>{i === 0 ? (g.lapse ? fmtDate(g.lapse) : "—") : (x.expdate ? fmtDate(x.expdate) : "")}</Td>
              <Td style={{ fontWeight: i === 0 ? 500 : 400 }}>{x.securedparty}
                {x.securedaddress && <div style={{ fontSize: 11, ...muted }}>{x.securedaddress}</div>}</Td>
              <Td>{x.debtorname}{x.debtoraddress && <div style={{ fontSize: 11, ...muted }}>{x.debtoraddress}</div>}</Td>
              <Td style={{ whiteSpace: "nowrap", textAlign: "right" }}>{i === 0 && <span style={{ display: "inline-flex", gap: 4 }}>
                {g.ours && <Pill tone={GREEN} title={g.oursWhy || "Matched to our filing by number"}>Ours</Pill>}
                {g.oursSet === "notours" && <Pill tone={GREY} title="Marked not ours on this search by a person">Not ours</Pill>}
                {role.canEdit && o.suggested_accountid && (g.oursSet
                  ? <a href="#" onClick={e => { e.preventDefault(); undoOurs(g); }} style={{ fontSize: 11, color: B.cyan, alignSelf: "center" }}>undo</a>
                  : <a href="#" onClick={e => { e.preventDefault(); setOurs(g, !g.ours); }} title={g.ours ? "Not our filing — take it off ours" : "Our filing — the register doesn't have it"}
                       style={{ fontSize: 11, color: B.cyan, alignSelf: "center" }}>{g.ours ? "not ours?" : "ours?"}</a>)}
                {g.terminated ? <Pill tone={GREY}>Terminated</Pill> : g.lapsed ? <Pill tone={GREY}>Lapsed</Pill> : <Pill tone={INFO}>Active</Pill>}
                {g.aheadOfUs && <Pill tone={RED}>Ahead of ours</Pill>}
              </span>}</Td>
            </tr>))}
        </Fragment>)}</tbody>
      </table>
      <div style={{ fontSize: 11.5, ...muted, marginTop: 6 }}>
        Lapse dates are worked out the way the filing office does when the report doesn't print one: five years from filing,
        five more for each continuation. Priority here is by filing date only — a purchase-money lien or an intercreditor
        agreement can change the order; mark those findings known.
      </div>
    </div>}

    {o.history && o.history.length > 0 && <div style={{ marginTop: 12 }}>
      <a href="#" onClick={e => { e.preventDefault(); setShowHist(x => !x); }} style={{ color: B.cyan, fontSize: 12 }}>
        {showHist ? "Hide CSC history" : "CSC history (" + o.history.length + ")"}</a>
      {showHist && o.history.map((h, i) => <div key={i} style={{ fontSize: 12 }}>
        <span style={muted}>{fmtWhen(h.at)}</span> · {h.what}{h.by ? <span style={muted}> · {h.by}</span> : null}</div>)}
    </div>}
  </Modal>;
};

// ── Debtor Tracking: CSC's "new filing found" notices ───────────────────────
// Read from the notices mailbox (CSC's API has no endpoint for them). A hit is
// only ours to look at when its debtor name is exactly one of our entities;
// the rest — the short tracking name catching other companies — sit collapsed
// under "Not our borrower" and are never counted.
const KIND_LABEL = { ours: "Our filing", termination: "Termination — not ours", change: "Change — not ours",
                     newlien: "New lien", other: "—" };
// Whether a debtor is our borrower is a portfolio manager's call. The page
// only SUGGESTS: an exact name match (raises its alert at once, marked
// unconfirmed), a possible match (first distinctive word in common — no alert
// until confirmed), or none ("Likely not ours"). Each answer is remembered per
// debtor name, so the same name is never asked twice.
// ── "On file" — our entities whose names look like the notice's debtor ──
// The debtor is compared with every borrower / guarantor entity: after
// dropping case, punctuation, legal endings and generic words (group,
// holdings, services …), the two must share a run of at least 7 letters
// ("heartland" in "Heartland Towing" and "Heartland Dental Holdings").
// Reference only: it helps a person decide, it links nothing.
const GENERIC = /\b(the|and|of|group|holdings?|services?|management|company|companies|enterprises?|partners?|capital|international|solutions?|industries|associates|systems|global|usa|america|national|properties|investments?|ventures?)\b/g;
const simKey = n => entKey(n).replace(GENERIC, " ").replace(/\s+/g, "");
const longestRun = (a, b) => {
  let best = 0, prev = new Array(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    const cur = new Array(b.length + 1).fill(0);
    for (let j = 1; j <= b.length; j++) if (a[i - 1] === b[j - 1]) { cur[j] = prev[j - 1] + 1; if (cur[j] > best) best = cur[j]; }
    prev = cur;
  }
  return best;
};
const SIM_MIN = 7;
// A name as compared: case, punctuation, spacing and LLC / Inc endings aside.
const entKey = n => String(n || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9 ]/g, " ")
  .replace(/\b(llc|l l c|inc|incorporated|corp|corporation|co|company|ltd|lp|llp|pllc|pc)\b/g, " ").replace(/\s+/g, " ").trim();
const TrackingCard = ({ data, q, canEdit, canRun, api, go, accounts, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [open, setOpen] = useState({ confirm: true });
  const [pick, setPick] = useState({});          // namekey -> accountid chosen in the picker
  const [pickEnt, setPickEnt] = useState({});    // namekey -> entity clicked under "on file" (shown ticked)
  // The borrower / guarantor entities of each account, shown beside the picker
  // so the debtor in CSC's notice can be compared with the entities' legal
  // names before deciding. For reference only — nothing is linked to an entity.
  const [entities, setEntities] = useState(null);
  useEffect(() => {
    api.get("getBorrowersGuarantors").then(d => {
      const m = {};
      (Array.isArray(d) ? d : []).forEach(e => { const k = String(e.accountid); (m[k] = m[k] || []).push(e); });
      setEntities(m);
    }).catch(() => setEntities({}));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <Spinner />;
  if (data.error) return <Card><div style={{ fontSize: 13, ...muted, padding: 8 }}>{data.error}</div></Card>;
  // Similar entities per debtor name, best first, at most three — worked out
  // once per name per render (the same name recurs across notices).
  const flatEnts = entities ? Object.values(entities).flat().map(e => ({ ...e, _k: simKey(e.borrowerguarantorname) })) : [];
  const simCache = {};
  const similar = name => {
    if (simCache[name]) return simCache[name];
    const k = simKey(name);
    const out = k.length < SIM_MIN ? [] : flatEnts.map(e => ({ e, n: e._k.length >= SIM_MIN ? longestRun(k, e._k) : 0 }))
      .filter(x => x.n >= SIM_MIN).sort((x, y) => y.n - x.n).slice(0, 3).map(x => x.e);
    return (simCache[name] = out);
  };
  const hits = localFilter(data.hits || [], q, ["debtorname", "companyname", "securedparty", "filingnumber", "csc_orderid", "criteria_name"]);
  const alerts = hits.filter(h => h.alert);
  const confirm = hits.filter(h => h.bucket === "confirm" && !h.alert);
  const ours = hits.filter(h => (h.bucket === "ours") && !h.alert);
  const likely = hits.filter(h => h.bucket === "likelynot");
  const notours = hits.filter(h => h.bucket === "notours");
  const post = async body => { setBusy(true);
    try { await api.post("saveCSCOrderReview", body); } catch (e) { alert(e.message); }
    setBusy(false); onChanged(); };
  const decide = (h, decision) => {
    const accountid = pick[h.namekey] || h.suggested_accountid;
    if (decision === "ours" && !accountid) { alert("Pick the borrower first."); return; }
    post({ action: "trackingDecision", items: [{ name: h.debtorname, decision, accountid }] });
  };
  const allNotOurs = () => {
    const names = [...new Set(likely.map(h => h.debtorname))];
    if (!window.confirm(`Confirm ${names.length} debtor name${names.length === 1 ? "" : "s"} as not our borrower? Each is remembered, so later notices for the same name are answered too.`)) return;
    post({ action: "trackingDecision", items: names.map(name => ({ name, decision: "notours" })) });
  };
  const run = async () => {
    setBusy(true); setMsg(null);
    try {
      const t = { notices: 0, hits: 0, matched: 0, scanned: 0 };
      for (let i = 0; i < 10; i++) {
        const x = await api.post("runCSCTrackingSync", {});
        ["notices", "hits", "matched", "scanned"].forEach(k => { t[k] += x[k] || 0; });
        if (!x.more) break;
      }
      setMsg(`${t.scanned} messages looked at · ${t.notices} new notice${t.notices === 1 ? "" : "s"} · ${t.hits} filings`);
    } catch (e) { setMsg(e.message); }
    setBusy(false); onChanged();
  };
  const ack = h => {
    const note = window.prompt("Why is this known / acceptable? (e.g. equipment lien, subordinated, filed by us through counsel)", "");
    if (!note) return;
    post({ action: "ackFinding", accountid: h.accountid, findingkey: h.findingkey, note,
           label: `${h.filingtype || "filing"} against ${h.debtorname}${h.securedparty ? ", secured party " + h.securedparty : ""}` });
  };
  const tone = h => h.ack ? GREY : h.level === "red" ? RED : h.level === "amber" ? AMBER : h.kind === "ours" ? GREEN : GREY;
  const suggestion = h => h.suggest === "exact" ? "Exact name match"
    : h.suggest === "possible" ? "Possible: " + (h.candidates || []).map(c => c.companyname).join(", ") : "No match";
  const decisionCell = h => {
    if (h.decision) return <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
      <span style={{ fontSize: 11, ...muted }}>{h.decision === "ours" ? "ours" : "not ours"}{h.decidedby ? " · " + h.decidedby : ""}</span>
      {canEdit && <Btn disabled={busy} onClick={() => post({ action: "clearTrackingDecision", name: h.debtorname })}>Undo</Btn>}
    </div>;
    if (!canEdit) return <span style={{ fontSize: 11, ...muted }}>{suggestion(h)}</span>;
    const val = pick[h.namekey] || h.suggested_accountid || "";
    return <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
      <select value={val} disabled={busy}
              onChange={e => { const v = e.target.value; setPick(p => ({ ...p, [h.namekey]: v }));
                               setPickEnt(p => ({ ...p, [h.namekey]: null })); }}
              style={{ padding: "3px 6px", fontSize: 12, borderRadius: 4, maxWidth: 190,
                       border: pick[h.namekey] ? "1.5px solid " + B.cyan : "1px solid #999" }}>
        <option value="">— borrower —</option>
        {(accounts || []).map(a => <option key={a.id} value={a.id}>{a.companyname}</option>)}
      </select>
      <Btn variant="primary" disabled={busy} onClick={() => decide(h, "ours")}>Our borrower</Btn>
      <Btn disabled={busy} onClick={() => decide(h, "notours")}>Not ours</Btn>
    </div>;
  };
  const row = h => <tr key={h.id}>
    <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(h.receivedat ? String(h.receivedat) : null)}</Td>
    <Td style={{ fontWeight: 500 }}>{h.debtorname}
      <div style={{ fontSize: 11, ...muted }}>{h.decision ? (h.accountid ? <a href="#" onClick={e => { e.preventDefault(); go("account", { accountid: h.accountid, companyname: h.companyname }); }}
           style={{ color: B.cyanDark }}>{h.companyname}</a> : "not our borrower")
        : pick[h.namekey] ? <span style={{ color: B.cyanDark, fontWeight: 600 }}>
            Picked: {((accounts || []).find(a => String(a.id) === String(pick[h.namekey])) || {}).companyname || "borrower"}
            <span style={{ fontWeight: 400, ...muted }}> — click Our borrower to confirm</span></span>
        : suggestion(h)}
        {h.accountid && !h.live ? " · " + (h.accountstatus || "not funded") : ""}</div>
      {/* Undecided only: our entities with a similar name. Clicking one picks its
          borrower in the dropdown — the decision is still yours. */}
      {!h.decision && similar(h.debtorname).length > 0 && (
        <div style={{ fontSize: 11, marginTop: 2, fontWeight: 400 }}>
          <span style={muted}>on file: </span>
          {similar(h.debtorname).map((e, i) => <Fragment key={e.id}>
            {i > 0 && <span style={muted}> · </span>}
            <a href="#" title={[e.companyname, e.borrowerguarantor].filter(Boolean).join(" · ") + (canEdit ? " — click to pick this borrower" : "")}
               onClick={ev => { ev.preventDefault(); if (!canEdit) return;
                                setPick(p => ({ ...p, [h.namekey]: String(e.accountid) }));
                                setPickEnt(p => ({ ...p, [h.namekey]: e.id })); }}
               style={{ color: B.cyanDark, textDecoration: "none", fontWeight: pickEnt[h.namekey] === e.id ? 700 : 400 }}>
              {pickEnt[h.namekey] === e.id ? "✓ " : ""}{e.borrowerguarantorname}</a>
            {/* The account, only when it is not the one in the dropdown — the
                usual case is the same account, and repeating it is noise. */}
            {String(e.accountid) !== String(pick[h.namekey] || h.suggested_accountid || "") && e.companyname &&
              <span style={muted}> ({e.companyname})</span>}
          </Fragment>)}
        </div>)}</Td>
    <Td>{h.accountid ? <Pill tone={tone(h)} title={h.ack ? "Known — " + (h.ack.note || "") : ""}>{KIND_LABEL[h.kind]}</Pill> : <span style={muted}>—</span>}
      {h.alert && !h.decision && <div style={{ fontSize: 10.5, color: AMBER.fg }}>unconfirmed match</div>}</Td>
    <Td>{h.filingtype || "—"}</Td>
    <Td style={mono}>{h.filingnumber}</Td>
    <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(h.filingdate)}</Td>
    <Td style={{ fontSize: 12 }}>{h.securedparty || "—"}</Td>
    <Td>{h.state || "—"}</Td>
    <Td style={{ ...mono, fontSize: 11.5 }} title={h.criteria_name ? "Tracking: " + h.criteria_name : ""}>{h.csc_orderid || "—"}</Td>
    <Td style={{ whiteSpace: "nowrap" }}><div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
      {decisionCell(h)}
      {canEdit && h.accountid && h.level !== "info" && (h.ack
        ? <Btn disabled={busy} onClick={() => post({ action: "clearAck", accountid: h.accountid, findingkey: h.findingkey })}>Re-open</Btn>
        : <Btn disabled={busy} onClick={() => ack(h)}>Mark known</Btn>)}
    </div></Td>
  </tr>;
  const head = <thead><tr><Th>Notice</Th><Th>Debtor (as filed)</Th><Th>Result</Th><Th>Filing type</Th>
    <Th>Filing #</Th><Th>Filed</Th><Th>Secured party</Th><Th>State</Th><Th>Tracking #</Th><Th></Th></tr></thead>;
  const section = (key, label, list, extra) => list.length > 0 && <Fragment>
    <Band colSpan={10} onClick={() => setOpen(o => ({ ...o, [key]: !o[key] }))}>
      <span>{open[key] ? "▾" : "▸"} {label} ({list.length})</span>
      {extra && <span style={{ marginLeft: 12, textTransform: "none", letterSpacing: 0 }} onClick={e => e.stopPropagation()}>{extra}</span>}
    </Band>
    {open[key] && list.map(row)}
  </Fragment>;
  const lr = data.lastRun;
  return <Card style={{ overflowX: "auto" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
      <span style={{ fontSize: 12, ...muted }}>
        CSC Debtor Tracking notices, read from {lr && lr.mailbox ? lr.mailbox : "the notices mailbox"}. Whether a debtor
        is our borrower is your call — the page only suggests, and remembers each answer by debtor name.
        {lr ? ` Last read ${fmtWhen(lr.finishedat || lr.startedat)}${lr.ranby ? " by " + lr.ranby : ""}${lr.error ? " — failed: " + lr.error : ""}.` : " Not read yet."}
        {data.counts && data.counts.unparsed ? ` ${data.counts.unparsed} notice(s) couldn't be read.` : ""}
      </span>
      {canRun && <span style={{ marginLeft: "auto" }}>
        <Btn variant="primary" disabled={busy || !data.mailboxSet} onClick={run}
             title={data.mailboxSet ? "" : "Set the CSC_TRACKING_MAILBOX app setting first"}>
          {busy ? "Working…" : "Read tracking notices"}</Btn></span>}
    </div>
    {msg && <div style={{ fontSize: 12.5, ...muted, marginBottom: 8 }}>{msg}</div>}
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
      {head}
      <tbody>
        {alerts.map(row)}
        {!alerts.length && <tr><td colSpan={10} style={{ padding: 12, fontSize: 13, ...muted }}>No open tracking alerts on funded borrowers.</td></tr>}
        {section("confirm", "Needs a decision — confirm the borrower", confirm)}
        {section("ours", "Our borrowers — ours, known, or not funded", ours)}
        {section("likely", "Likely not ours — no name match", likely,
          canEdit && likely.length > 0 && <Btn disabled={busy} onClick={allNotOurs}>Confirm all as not ours</Btn>)}
        {section("notours", "Confirmed not ours", notours)}
      </tbody>
    </table>
  </Card>;
};

// ═════════════════════════════════ THE PAGE ═════════════════════════════════
export const UCCSearchesPage = () => {
  const { api, role, go } = useApp();
  const fresh = useMonitorFreshness();   // "Last CSC pull" / "Last tracking read" tiles
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("borrowers");   // borrowers | review | findings | stale | all
  const [accounts, setAccounts] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [showOld, setShowOld] = useState({});      // borrower -> show superseded searches too
  const [closed, setClosed] = useState({});
  const [reading, setReading] = useState(null);    // progress of "Read reports"
  const [busy, setBusy] = useState(false);

  // Debtor Tracking notices — loaded on their own so the tables not existing
  // yet (SQL not run) never blanks the searches.
  const [tracking, setTracking] = useState(null);
  const loadTracking = () => api.get("getCSCTracking").then(d => setTracking(d || null))
    .catch(e => setTracking({ error: e.message }));
  const load = () => { loadTracking(); return api.get("getUCCSearches")
    .then(d => { setData(d || {}); setErr(null); })
    .catch(e => setErr(e.message)); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (role.canEdit) api.get("getAccounts").then(d => setAccounts((Array.isArray(d) ? d : [])
      .sort((a, b) => String(a.companyname).localeCompare(String(b.companyname))))).catch(() => {});
  }, [role.canEdit]);

  // Reads every unread report, ~35 seconds per call, until none are left.
  const readAll = async () => {
    const t = { read: 0, failed: 0, nodoc: 0, remaining: null, errors: [] };
    setReading({ ...t, running: true });
    try {
      for (let guard = 0; guard < 60; guard++) {
        const x = await api.post("readUCCSearchReport", { batch: true });
        t.read += x.read || 0; t.failed += x.failed || 0; t.nodoc += x.nodoc || 0; t.remaining = x.remaining;
        t.errors = [...t.errors, ...(x.errors || [])].slice(0, 10);
        setReading({ ...t, running: true });
        if (!x.remaining || !((x.read || 0) + (x.nodoc || 0) + (x.failed || 0))) break;
      }
    } catch (e) { t.errors.push(e.message); }
    setReading({ ...t, running: false });
    load();
  };
  const confirmAll = async ids => {
    if (!window.confirm(`Confirm the matched borrower on ${ids.length} search${ids.length === 1 ? "" : "es"}?`)) return;
    setBusy(true);
    try { await api.post("saveCSCOrderReview", { action: "confirmSearches", ids }); } catch (e) { alert(e.message); }
    setBusy(false); load();
  };
  const [showExcused, setShowExcused] = useState(false);
  const [openF, setOpenF] = useState({});          // Open findings: borrower -> expanded
  const staleAck = b => {
    const note = window.prompt(`Why don't we run UCC searches on ${b.companyname}? (e.g. participation — the lead lender monitors; unsecured)`, "");
    if (note === null) return;
    if (!note.trim()) { alert("A reason is required."); return; }
    quick({}, { action: "ackFinding", accountid: b.accountid, findingkey: "stale:-", note: note.trim() });
  };
  const staleClear = b => quick({}, { action: "clearAck", accountid: b.accountid, findingkey: "stale:-" });
  const quick = async (o, body) => {
    setBusy(true);
    try { await api.post("saveCSCOrderReview", { csc_orderid: o.csc_orderid, ...body }); } catch (e) { alert(e.message); }
    setBusy(false); load();
  };

  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!data) return <Spinner />;
  const rows = data.rows || [];
  const c = data.counts || {};
  const borrowers = data.borrowers || [];
  const excusedCount = borrowers.filter(b => b.staleack).length;
  const text = o => [o.debtornames, o.suggested_company, o.jurisdiction_state, o.csc_orderid, o.billingref,
                     o.searchedby, o.cscuser, o.analysis && o.analysis.headline].filter(Boolean).join(" ");
  const rowsQ = localFilter(rows.map(o => ({ ...o, _text: text(o) })), q, ["_text"]);
  const open = rows.find(o => o.csc_orderid === openId) || null;
  const toReview = rowsQ.filter(o => o.needsreview && !o.notapplicable && !isNoReport(o));
  // Searches the name match could not place — a person has to pick the borrower.
  const noMatchCount = rows.filter(o => o.needsreview && !o.notapplicable && !o.suggested_accountid && !isNoReport(o)).length;
  const suggestedIds = toReview.filter(o => o.suggested_accountid).map(o => o.csc_orderid);

  const findingRows = rowsQ.filter(o => o.latest && o.analysis && o.analysis.read === "ok" && o.analysis.mode === "monitoring"
                                     && ["Accrual", "Non Accrual"].includes(o.accountstatus))   // funded borrowers only
    .flatMap(o => o.analysis.findings.filter(f => !f.acked && (f.level === "red" || f.level === "amber")).map(f => ({ o, f })))
    .sort((x, y) => (x.f.level === "red" ? 0 : 1) - (y.f.level === "red" ? 0 : 1) || String(x.o.suggested_company).localeCompare(String(y.o.suggested_company)));
  // Grouped by borrower, collapsed to one line each: borrowers with a red
  // finding first, then by name.
  const findingGroups = (() => {
    const m = new Map();
    for (const x of findingRows) {
      const k = String(x.o.suggested_accountid);
      if (!m.has(k)) m.set(k, { id: k, name: x.o.suggested_company, status: x.o.accountstatus, list: [] });
      m.get(k).list.push(x);
    }
    return [...m.values()].map(g => ({ ...g, red: g.list.filter(x => x.f.level === "red").length,
                                       states: [...new Set(g.list.map(x => x.o.jurisdiction_state).filter(Boolean))].sort() }))
      .sort((a, b) => (b.red > 0) - (a.red > 0) || String(a.name).localeCompare(String(b.name)));
  })();
  const allFOpen = findingGroups.length > 0 && findingGroups.every(g => openF[g.id]);

  const bq = q.trim() ? new Set(rowsQ.map(o => String(o.suggested_accountid))) : null;
  // Not searched in 12 mo lists the open ones; those with a reason on record
  // sit collapsed at the bottom of the same list.
  const bList = borrowers.filter(b => (mode !== "stale" || b.stale || (showExcused && b.staleack))
    && (!bq || bq.has(String(b.accountid)) || String(b.companyname).toLowerCase().includes(q.trim().toLowerCase())));
  const byStatus = new Map();
  for (const b of bList) { const st = b.accountstatus || "Unknown"; if (!byStatus.has(st)) byStatus.set(st, []); byStatus.get(st).push(b); }
  const statusGroups = [...byStatus.entries()].sort((a, b) => statusRank(a[0]) - statusRank(b[0]) || a[0].localeCompare(b[0]))
    .map(([status, list]) => ({ status, list: list.sort((a, b) => String(a.companyname).localeCompare(String(b.companyname))) }));
  const searchesOf = id => rowsQ.filter(o => String(o.suggested_accountid) === String(id) && o.reviewnote !== "not a borrower");

  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={setQ} placeholder="Search borrower, name searched, order #, state..." />,
      <Btn key="ex" onClick={() => exportCSV(rowsQ.map(o => ({ ...o, searchedasof: searchDate(o),
              result: o.analysis && o.analysis.headline, openfindings: o.analysis && o.analysis.open })),
            ["searchedasof", "csc_orderid", "jurisdiction_state", "debtornames", "suggested_company", "result", "openfindings",
             "searchedby", "billingref"], "ucc_searches.csv")}>Export CSV</Btn>,
      role.canEdit && data.tablesReady && <Btn key="rd" variant="primary" disabled={!!(reading && reading.running)} onClick={readAll}>
        {reading && reading.running ? "Reading reports…" : `Read reports${c.unread ? " (" + c.unread + ")" : ""}`}</Btn>,
    ].filter(Boolean)} />

    {!data.tablesReady && <div style={{ fontSize: 13, color: "#7a5200", background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                                         borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
      <strong>Search reports can't be read yet.</strong> Run <code>sql/2026-10-15_ucc_search_reports.sql</code> once;
      the searches below can be tied to borrowers in the meantime.</div>}

    {reading && <div style={{ fontSize: 12.5, background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)",
                              borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
      {reading.running ? "Reading reports from CSC… " : "Done. "}
      {reading.read} read · {reading.nodoc} with no report · {reading.failed} couldn't be read
      {reading.remaining ? ` · ${reading.remaining} left` : ""}
      {reading.errors.length > 0 && <div style={{ ...muted, marginTop: 4 }}>{reading.errors.map((e, i) => <div key={i}>{e}</div>)}</div>}
    </div>}

    <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
      <Tile label="Borrowers" value={borrowers.length} selected={mode === "borrowers"} onClick={() => setMode("borrowers")} />
      <Tile label="To tie out" value={c.review || 0} tone="#FDF0D5" selected={mode === "review"} onClick={() => setMode("review")}
            hint="Searches not yet confirmed against a borrower" />
      <Tile label="No match" value={noMatchCount} tone="#FBE4DD" selected={mode === "nomatch"}
            onClick={() => setMode(m => m === "nomatch" ? "review" : "nomatch")}
            hint="Searches whose name matched no borrower — pick the borrower, or mark it not a borrower" />
      <Tile label="Open findings" value={c.findings || 0} tone="#FBE4DD" selected={mode === "findings"} onClick={() => setMode("findings")}
            hint="From each borrower's latest search per state" />
      <Tile label="Not searched in 12 mo" value={c.stale || 0} tone="#FDF0D5" selected={mode === "stale"} onClick={() => setMode("stale")}
            hint="Live borrowers whose last search is over a year old, or who have none" />
      <Tile label="All searches" value={c.total || 0} selected={mode === "all"} onClick={() => setMode("all")} />
      <Tile label="Tracking alerts" value={tracking && tracking.counts ? tracking.counts.alerts : "—"} tone="#FBE4DD"
            selected={mode === "tracking"}
            onClick={() => setMode(m => m === "tracking" ? "borrowers" : "tracking")}
            hint="New filings CSC's Debtor Tracking found against our borrowers" />
      {/* Searches arrive with the CSC pull; tracking notices with the mailbox read.
          Both run every morning — red after 48 hours or a failed run. */}
      <LastRunTile label="Last CSC pull" run={runOf.csc(fresh)} staleHours={48} />
      <LastRunTile label="Last tracking read" run={runOf.tracking(fresh)} staleHours={48} />
    </div>

    {(mode === "review" || mode === "nomatch") && <Card style={{ overflowX: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, ...muted }}>
          Each search is matched to a borrower from the name searched. Confirm the match, change it, or mark a prospect
          that never closed as not a borrower. A search counts toward a borrower's monitoring only once it is tied out.
        </span>
        {role.canEdit && mode === "review" && suggestedIds.length > 0 &&
          <span style={{ marginLeft: "auto" }}><Btn disabled={busy} onClick={() => confirmAll(suggestedIds)}>Confirm all matched ({suggestedIds.length})</Btn></span>}
      </div>
      {!(mode === "nomatch" ? toReview.filter(o => !o.suggested_accountid).length : toReview.length)
        ? <div style={{ fontSize: 13, ...muted, padding: 12 }}>{mode === "nomatch" ? "Every search has a borrower match." : "Every search is tied out."}</div> :
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead><tr><Th>Completed</Th><Th>CSC Order #</Th><Th>State</Th><Th>Name searched</Th><Th>Borrower</Th><Th>Result</Th><Th>Billing ref</Th><Th></Th></tr></thead>
          <tbody>{[["Matched from the name — confirm", mode === "nomatch" ? [] : toReview.filter(o => o.suggested_accountid)],
                   ["No match — pick the borrower", toReview.filter(o => !o.suggested_accountid)]].map(([label, list]) =>
            list.length ? <Fragment key={label}>
              <Band colSpan={8}>{label} ({list.length})</Band>
              {list.map(o => <tr key={o.csc_orderid}>
                <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(o.completed || o.filingdate)}</Td>
                <Td style={mono}>{orderNo(o.csc_orderid)}</Td>
                <Td>{o.jurisdiction_state || "—"}</Td>
                <Td style={{ fontWeight: 500 }}>{o.debtornames || "—"}</Td>
                <Td>{role.canEdit
                  ? <select value={o.suggested_accountid || ""} disabled={busy}
                            onChange={e => quick(o, { action: "assignSearch", accountid: e.target.value || null })}
                            style={{ padding: "4px 6px", fontSize: 12, borderRadius: 4, border: "1px solid #999", maxWidth: 240 }}>
                      <option value="">— pick a borrower —</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.companyname}</option>)}
                    </select>
                  : (o.suggested_company || "—")}</Td>
                <Td><SearchResult o={o} /></Td>
                <Td>{o.billingref || "—"}</Td>
                <Td style={{ whiteSpace: "nowrap" }}><div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  {role.canEdit && o.suggested_accountid && <Btn variant="primary" disabled={busy}
                     onClick={() => quick(o, { action: "confirmSearches", ids: [o.csc_orderid], csc_orderid: undefined })}>Confirm</Btn>}
                  {role.canEdit && <Btn disabled={busy} onClick={() => quick(o, { action: "assignSearch", accountid: null })}>Not a borrower</Btn>}
                  {role.canEdit && <Btn disabled={busy} title="Run by mistake (wrong name, wrong state) — keep it, but count it nowhere"
                     onClick={() => { const note = window.prompt("Why doesn't this search apply?", "Run by mistake — wrong name");
                                      if (note !== null) quick(o, { action: "notApplicable", note }); }}>Not applicable</Btn>}
                  <Btn onClick={() => setOpenId(o.csc_orderid)}>Open</Btn>
                </div></Td>
              </tr>)}
            </Fragment> : null)}</tbody>
        </table>}
    </Card>}

    {mode === "findings" && <Card style={{ overflowX: "auto" }}>
      <div style={{ fontSize: 12, ...muted, marginBottom: 8 }}>
        Funded borrowers (Accrual, Non Accrual) only, from each one's latest read search in each state — click a borrower to see its findings. A known, accepted lien (subordinated, equipment-only)
        can be marked known in the search — it then stays off this list on later searches too.
      </div>
      {!findingRows.length ? <div style={{ fontSize: 13, ...muted, padding: 12 }}>No open findings on the latest searches of funded borrowers.</div> : <>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
          <a href="#" onClick={e => { e.preventDefault(); setOpenF(allFOpen ? {} : Object.fromEntries(findingGroups.map(g => [g.id, true]))); }}
             style={{ fontSize: 12, color: B.cyan }}>{allFOpen ? "Collapse all" : "Expand all"}</a>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead><tr><Th>Borrower</Th><Th>State</Th><Th>Results as of</Th><Th>Finding</Th><Th></Th></tr></thead>
          <tbody>{findingGroups.map(g => {
            const isOpen = !!openF[g.id];
            return <Fragment key={g.id}>
              <tr style={{ cursor: "pointer", background: `color-mix(in srgb, ${B.cyanLight} 45%, transparent)` }}
                  onClick={() => setOpenF(x => ({ ...x, [g.id]: !x[g.id] }))}>
                <Td style={{ fontWeight: 600 }}><span style={{ marginRight: 6, ...muted }}>{isOpen ? "▾" : "▸"}</span>
                  <span style={{ color: B.cyanDark }}>{g.name}</span>
                  {g.status === "Non Accrual" && <span style={{ marginLeft: 8 }}><Badge s={g.status} /></span>}</Td>
                <Td style={muted}>{g.states.join(", ")}</Td>
                <Td></Td>
                <Td>
                  <span style={{ fontWeight: 600 }}>{g.list.length} finding{g.list.length === 1 ? "" : "s"}</span>
                  {g.red > 0 && <span style={{ marginLeft: 8, color: RED.fg, fontWeight: 600 }}>{g.red} red</span>}
                  {g.list.length - g.red > 0 && <span style={{ marginLeft: 8, color: AMBER.fg }}>{g.list.length - g.red} amber</span>}
                </Td>
                <Td></Td>
              </tr>
              {isOpen && g.list.map(({ o, f }) => (
                <tr key={o.csc_orderid + f.key} style={{ cursor: "pointer" }} onClick={() => setOpenId(o.csc_orderid)}>
                  <Td></Td>
                  <Td>{o.jurisdiction_state}</Td>
                  <Td style={{ whiteSpace: "nowrap" }}>{fmtDate(searchDate(o))}</Td>
                  <Td style={{ color: f.level === "red" ? RED.fg : AMBER.fg }}>{f.text}</Td>
                  <Td style={{ textAlign: "right" }}><span style={{ color: B.cyan, fontSize: 12 }}>open →</span></Td>
                </tr>))}
            </Fragment>;
          })}</tbody>
        </table></>}
    </Card>}

    {mode === "tracking" && <TrackingCard data={tracking} q={q} canEdit={role.canEdit} canRun={!!role.isAdmin} api={api} go={go} accounts={accounts} onChanged={loadTracking} />}

    {mode === "all" && <Card style={{ overflowX: "auto" }}>
      <SearchTable rows={rowsQ} onOpen={o => setOpenId(o.csc_orderid)} showBorrower />
    </Card>}

    {(mode === "borrowers" || mode === "stale") && <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead><tr><Th>Borrower</Th><Th>Last searched</Th><Th>Searches</Th><Th>Latest result by state</Th><Th>Open findings</Th><Th></Th></tr></thead>
        <tbody>{statusGroups.map(g => {
          // Accrual and Non Accrual open; everything else (Paid Off …) starts collapsed.
          const isClosed = closed[g.status] != null ? !!closed[g.status] : !["Accrual", "Non Accrual"].includes(g.status);
          return <Fragment key={g.status}>
            <Band colSpan={6} onClick={() => setClosed(x => ({ ...x, [g.status]: !isClosed }))}>
              <span style={{ marginRight: 6 }}>{isClosed ? "▸" : "▾"}</span><Badge s={g.status} />
              <span style={{ marginLeft: 8, fontSize: 11 }}>{g.list.length} borrower{g.list.length === 1 ? "" : "s"}</span>
            </Band>
            {!isClosed && g.list.map(b => {
              const isOpen = !!expanded[b.accountid];
              const all = isOpen ? searchesOf(b.accountid) : [];
              const current = currentOnly(all);
              const older = all.length - current.length;
              const mine = showOld[b.accountid] ? all : current;
              // Open, the borrower's row takes a light ribbon and a blue edge that
              // runs down the panel below it, so the open borrower reads as one
              // block and the closed ones around it stay plainly separate.
              const ribbon = `color-mix(in srgb, ${B.cyanLight} 45%, transparent)`;
              return <Fragment key={b.accountid}>
                <tr style={{ cursor: "pointer", background: isOpen ? ribbon : undefined }}
                    onClick={() => setExpanded(x => ({ ...x, [b.accountid]: !x[b.accountid] }))}
                    onMouseEnter={e => { e.currentTarget.style.background = isOpen ? ribbon : "var(--color-background-secondary)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isOpen ? ribbon : ""; }}>
                  <Td style={{ fontWeight: 600, paddingLeft: isOpen ? 19 : 22,
                               borderLeft: isOpen ? "3px solid " + B.cyan : undefined }}>
                    {/* The whole row expands, name included. The link to the account
                        used to be the name itself, a few pixels from the arrow, so
                        "expand" kept turning into "leave the page". It now lives at the
                        top of the open panel. */}
                    <span style={{ display: "inline-block", width: 14, marginRight: 6, ...muted }}>{isOpen ? "▾" : "▸"}</span>
                    <span style={{ color: B.cyanDark }}>{b.companyname}</span></Td>
                  <Td style={{ whiteSpace: "nowrap" }}>{b.lastsearch ? fmtDate(b.lastsearch) : <span style={muted}>never</span>}
                    {b.stale && <span style={{ marginLeft: 6 }}><Pill tone={AMBER}>{b.lastsearch ? "over 12 mo" : "no search"}</Pill></span>}
                    {b.staleack && <span style={{ marginLeft: 6 }} title={`${b.staleack.note || "No note"} — ${b.staleack.by || ""}`}>
                      <Pill tone={GREY}>no search needed</Pill></span>}</Td>
                  <Td>{b.searches || 0}{b.unread ? <span style={{ fontSize: 11, ...muted }}> · {b.unread} unread</span> : null}
                    {b.untied ? <span style={{ fontSize: 11, color: AMBER.fg }}> · {b.untied} to tie out</span> : null}</Td>
                  {/* One result per state — the latest read search in each — most serious first. */}
                  <Td>{b.results && b.results.length
                    ? <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                        {b.results.map(x => <span key={x.state + x.csc_orderid} title={`Results as of ${fmtDate(x.asOf)} · CSC order ${orderNo(x.csc_orderid)}`}>
                          <span style={{ fontSize: 11, fontWeight: 600, ...muted, marginRight: 6 }}>{x.state || "—"}</span>
                          <Pill tone={TONE[x.tone] || GREY}>{x.headline}</Pill></span>)}
                      </div>
                    : b.headline ? <Pill tone={TONE[b.tone] || GREY}>{b.headline}</Pill> : <span style={muted}>—</span>}</Td>
                  <Td style={{ color: b.red ? RED.fg : b.open ? AMBER.fg : undefined, fontWeight: b.open ? 600 : 400 }}>{b.open || "—"}</Td>
                  {/* Not searched in 12 months: a reason takes it off the list. */}
                  <Td style={{ whiteSpace: "nowrap", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                    {role.canEdit && b.stale && <Btn disabled={busy} title="A reason we don't run searches on this borrower — takes it off Not searched in 12 mo"
                      onClick={() => staleAck(b)}>No search needed</Btn>}
                    {role.canEdit && b.staleack && mode === "stale" && <Btn disabled={busy} onClick={() => staleClear(b)}>Re-open</Btn>}
                  </Td>
                </tr>
                {isOpen && <tr><td colSpan={6} style={{ padding: "12px 14px 16px 22px", background: "var(--color-background-secondary)",
                                                       borderLeft: "3px solid " + B.cyan,
                                                       borderBottom: "1.5px solid var(--color-border-secondary)" }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                    <a href="#" onClick={e => { e.preventDefault(); go("account", { accountid: b.accountid, companyname: b.companyname }); }}
                       style={{ fontSize: 12.5, fontWeight: 600, color: B.cyanDark, textDecoration: "none" }}>
                      Open {b.companyname} →</a>
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                    {/* What we actually filed, beside what was searched — so a search on
                        the wrong name or in the wrong state stands out without opening
                        UCC Filings. */}
                    <Card style={{ flex: "0 0 270px", padding: 12, borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                      <div style={panelTitle}>Our filings · {(b.filings || []).length}</div>
                      {(b.filings || []).length ? b.filings.map(f => {
                        const lapsed = /lapsed/i.test(f.status || "");
                        return <div key={(f.number || "") + f.state + f.debtor} title={f.status || ""}
                                    style={{ display: "flex", gap: 8, padding: "6px 0", fontSize: 12.5, opacity: lapsed ? 0.6 : 1,
                                             borderTop: "0.5px solid var(--color-border-tertiary)" }}>
                          <strong style={{ minWidth: 22 }}>{f.state || "—"}</strong>
                          <span style={{ flex: 1, minWidth: 0 }}>{f.debtor || "—"}
                            <div style={{ ...mono, fontSize: 11.5, ...muted }}>{f.number || "no number"}{lapsed ? " · lapsed" : ""}</div></span>
                        </div>;
                      }) : <div style={{ fontSize: 12.5, ...muted }}>None in the register.</div>}
                    </Card>
                    <Card style={{ flex: "1 1 560px", minWidth: 0, padding: 12, borderRadius: 8, overflowX: "auto",
                                   boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                      <div style={panelTitle}>Searches</div>
                      {mine.length ? <SearchTable rows={mine} onOpen={o => setOpenId(o.csc_orderid)} />
                        : <div style={{ fontSize: 12.5, ...muted }}>No searches tied to this borrower. Order one on CSC's website; it arrives on the next Pull from CSC.</div>}
                      {older > 0 && <a href="#" onClick={e => { e.preventDefault(); setShowOld(x => ({ ...x, [b.accountid]: !x[b.accountid] })); }}
                         style={{ display: "inline-block", marginTop: 6, fontSize: 12, color: B.cyan }}>
                        {showOld[b.accountid] ? "Hide older searches" : `Show ${older} older search${older === 1 ? "" : "es"}`}</a>}
                    </Card>
                  </div>
                </td></tr>}
              </Fragment>;
            })}
          </Fragment>;
        })}</tbody>
      </table>
      {!bList.length && <div style={{ fontSize: 13, ...muted, padding: 20, textAlign: "center" }}>
        {mode === "stale" ? "Every live borrower has been searched in the last 12 months, or has a reason on record." : "No borrowers match."}</div>}
      {mode === "stale" && excusedCount > 0 && <div onClick={() => setShowExcused(x => !x)}
          style={{ cursor: "pointer", marginTop: 10, display: "flex", alignItems: "baseline", gap: 10, padding: "6px 12px",
                   background: "var(--color-background-secondary)", borderRadius: 4 }}>
        <strong style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", ...muted }}>
          {showExcused ? "▾ Hide" : "▸ Show"} reason on record ({excusedCount})</strong>
        <span style={{ fontSize: 11.5, ...muted }}>Not searched in 12 months, with a reason recorded — not counted. Hover the grey tag for the reason; Re-open puts one back.</span>
      </div>}
    </Card>}

    {open && <SearchDetail o={open} accounts={accounts} onClose={() => { setOpenId(null); load(); }} onChanged={load} />}
  </>;
};
